import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Tag, Gift, Percent, Calendar, PartyPopper, Fish, Truck, Clock, Phone, type LucideIcon } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

type Deal = {
  icon: LucideIcon;
  title: string;
  description: string;
  badge: string;
  points?: string[];
  details: string;
  whatsappText: string;
};

const deals: Deal[] = [
  {
    icon: Fish,
    title: "Fish Steak – Freshly Grilled",
    description: "Freshly grilled fish steaks bursting with flavor. Available daily 11:30 AM – 11:30 PM at Sizzlers Jhelum Cantt.",
    badge: "Chef Special",
    points: [
      "Freshly grilled, never frozen",
      "Served with lemon, herbs & house sauce",
      "Available 11:30 AM – 11:30 PM",
      "Order via WhatsApp 0330-3628777",
      "A customer favourite — featured in real diner reels",
      "Perfect for ladies' night & friends' get-togethers",
      "Pairs beautifully with our Monin mocktail bar",
    ],
    details:
      "Our signature Fish Steak is hand-selected each morning, marinated in chef's special spices, and grilled to perfection over an open flame. Served with sautéed vegetables, garlic butter rice, lemon wedges, fresh herbs, and our signature house sauce. A must-try Chef Special at Sizzlers Restaurant, Main GT Road, Jhelum Cantt.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20to%20order%20Fish%20Steak.",
  },
  {
    icon: Flame,
    title: "Sizzler Platter Combo",
    description: "Mixed BBQ Platter + 2 Soft Drinks + Naan basket at a special bundle price. Available daily 6 PM – 11 PM.",
    badge: "Hot Deal",
    points: [
      "Mixed BBQ Platter (chicken, beef, mutton)",
      "2 chilled soft drinks included",
      "Fresh naan basket on the side",
      "Available every evening 6 PM – 11 PM",
    ],
    details:
      "Our most loved evening combo — a sizzling Mixed BBQ Platter loaded with chicken tikka, seekh kebab, beef boti and mutton chops, paired with two soft drinks and a basket of fresh tandoori naans. Perfect for couples or small groups looking for a complete BBQ experience.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20the%20Sizzler%20Platter%20Combo.",
  },
  {
    icon: Gift,
    title: "WhatsApp Order Bonus",
    description: "Order via WhatsApp on 0330-3628777 above Rs. 2,500 and get free home delivery within Jhelum city.",
    badge: "Free Delivery",
    points: [
      "Order above Rs. 2,500 via WhatsApp",
      "Free delivery within Jhelum city",
      "Skip the queue — order from home",
      "Quick confirmation on WhatsApp 0330-3628777",
    ],
    details:
      "Skip the wait and order directly via WhatsApp on 0330-3628777. On every order above Rs. 2,500, enjoy completely FREE home delivery within Jhelum city limits. Our team confirms your order on WhatsApp within minutes and delivers it hot and fresh to your doorstep.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20to%20place%20a%20WhatsApp%20order%20with%20free%20delivery.",
  },
  {
    icon: PartyPopper,
    title: "Privilege Card Discount",
    description: "Show your Sizzlers Privilege Card and enjoy exclusive member discounts on every visit.",
    badge: "Member Only",
    points: [
      "Exclusive member-only discounts",
      "Valid on every visit, all year round",
      "Special birthday & anniversary perks",
      "Priority reservations & seating",
    ],
    details:
      "The Sizzlers Privilege Card is our way of thanking loyal guests. Members enjoy exclusive discounts on every visit, special birthday & anniversary perks, priority seating and reservation, and early access to seasonal campaigns. Ask our front desk for details on how to get yours.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20to%20know%20about%20the%20Privilege%20Card.",
  },
];

type CampaignPoint = { icon: LucideIcon; text: string; details: string };

type Campaign = {
  id: string;
  title: string;
  badge: string;
  description: string;
  highlight: string;
  whatsappText: string;
  points: CampaignPoint[];
};

const campaigns: Campaign[] = [
  {
    id: "sizzler-pizza",
    title: "Sizzler Special Pizza",
    badge: "Live • Chef Special",
    description:
      "Sink your teeth into our signature Sizzler Special Pizza — wood-fired, loaded with premium toppings, double mozzarella and our chef's secret house sauce. Order today and enjoy ",
    highlight: "FREE delivery.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20to%20order%20the%20Sizzler%20Special%20Pizza.",
    points: [
      {
        icon: Flame,
        text: "Wood-fired, loaded with premium toppings",
        details:
          "Our Sizzler Special Pizza is hand-stretched and baked in a wood-fired oven, loaded with chicken tikka, beef pepperoni, fresh capsicum, onions, olives, jalapeños and a generous double layer of mozzarella cheese for that perfect cheesy pull.",
      },
      {
        icon: Percent,
        text: "Signature house sauce & double cheese",
        details:
          "Topped with our signature in-house pizza sauce made from slow-cooked tomatoes, garlic and Italian herbs, and finished with a double layer of mozzarella that melts into every bite.",
      },
      {
        icon: Clock,
        text: "Freshly baked to order — ready in 15 minutes",
        details:
          "Every Sizzler Special Pizza is prepared and baked fresh after you order. No pre-made bases, no reheating — just a hot, crispy pizza ready in about 15 minutes for dine-in, takeaway or delivery.",
      },
      {
        icon: Phone,
        text: "Order: WhatsApp 0330-3628777 or call 0544-628777",
        details:
          "Place your Sizzler Special Pizza order via WhatsApp on 0330-3628777 or call 0544-628777. Our team confirms instantly and dispatches piping hot to your doorstep within Jhelum city.",
      },
      {
        icon: PartyPopper,
        text: "A real customer favourite — captured in dining reels",
        details:
          "Featured again and again in our customers' own videos — from family dinners to ladies' night gatherings. The Sizzler Special Pizza is the slice everyone reaches for first at the table.",
      },
      {
        icon: Gift,
        text: "Pairs perfectly with fries, pasta & Monin mocktails",
        details:
          "Make it a full feast — add crispy golden fries, our chef's spicy chicken pasta, and a refreshing mocktail from our full Monin syrup bar (strawberry, blue curacao, mojito, kiwi & more) for the complete Sizzlers experience.",
      },
    ],
  },
  {
    id: "kheer",
    title: "Kheer – Heavenly Dessert Delight",
    badge: "Live • Sweet Special",
    description:
      "Treat yourself to our signature Kheer — slow-cooked rice pudding topped with almonds, pistachios and aromatic cardamom. Order today and enjoy ",
    highlight: "FREE home delivery.",
    whatsappText: "Hi%20Sizzlers%2C%20I%20want%20to%20order%20Kheer.",
    points: [
      {
        icon: Truck,
        text: "FREE home delivery within Jhelum city",
        details:
          "Enjoy completely free home delivery on every Kheer order within Jhelum city limits — no minimum order required for this sweet treat.",
      },
      {
        icon: Flame,
        text: "Slow-cooked, rich & creamy traditional recipe",
        details:
          "Our Kheer is slow-cooked for hours over a low flame using full-cream milk, premium basmati rice, sugar, cardamom and a generous garnish of almonds and pistachios for that authentic homemade taste.",
      },
      {
        icon: Clock,
        text: "Freshly prepared daily — order anytime",
        details:
          "Prepared fresh every single day in our kitchen. Order any time during our service hours and we'll send it out chilled and ready to enjoy.",
      },
      {
        icon: Phone,
        text: "Order: WhatsApp 0330-3628777 or call 0544-628777",
        details:
          "Place your Kheer order via WhatsApp on 0330-3628777 or call us directly on 0544-628777. Confirmation and delivery within minutes inside Jhelum city.",
      },
    ],
  },
];

type ActiveDetail =
  | { kind: "deal"; deal: Deal }
  | { kind: "point"; point: CampaignPoint; campaign: Campaign }
  | null;

const DealsCampaignSection = () => {
  const [swapped, setSwapped] = useState(false);
  const [active, setActive] = useState<ActiveDetail>(null);

  useEffect(() => {
    const id = window.setInterval(() => setSwapped((v) => !v), 30000);
    return () => window.clearInterval(id);
  }, []);

  const dealsPanel = (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-sizzle-orange/15 flex items-center justify-center">
          <Tag className="w-5 h-5 text-sizzle-orange" />
        </div>
        <h3 className="text-2xl font-heading font-bold">Exclusive Offers</h3>
      </div>

      <div className="space-y-4">
        {deals.map((deal) => (
          <article
            key={deal.title}
            onClick={() => setActive({ kind: "deal", deal })}
            className="cursor-pointer rounded-xl border border-border bg-background/50 p-4 hover:border-primary/40 hover:bg-background/80 transition-colors text-left w-full"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActive({ kind: "deal", deal });
              }
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <deal.icon className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="font-semibold text-base">{deal.title}</h4>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sizzle-orange/15 text-sizzle-orange">
                    {deal.badge}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {deal.description}
                </p>
                {deal.points && (
                  <ul className="mt-2 space-y-1">
                    {deal.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-xs text-foreground/85">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sizzle-orange shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button variant="sizzle" asChild>
          <Link to="/menu">Browse Menu</Link>
        </Button>
        <Button variant="outline" asChild>
          <a
            href="https://wa.me/923303628777?text=Hi%20Sizzlers%2C%20I%20want%20to%20use%20a%20deal."
            target="_blank"
            rel="noopener noreferrer"
          >
            Claim on WhatsApp
          </a>
        </Button>
      </div>
    </div>
  );

  const campaignPanel = (
    <div className="rounded-2xl border border-sizzle-orange/40 bg-gradient-to-br from-sizzle-red/15 via-card to-sizzle-orange/10 p-6 md:p-8 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-sizzle-orange/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
            <PartyPopper className="w-5 h-5 text-primary" />
          </div>
          <h3 className="text-2xl font-heading font-bold">Running Campaigns</h3>
        </div>

        <div className="space-y-6">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-xl border border-sizzle-orange/30 bg-background/40 p-5"
            >
              <span className="inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-sizzle-orange text-white mb-3">
                {campaign.badge}
              </span>

              <h4 className="text-xl md:text-2xl font-heading font-bold mb-2 leading-tight">
                {campaign.title}
              </h4>

              <p className="text-muted-foreground leading-relaxed mb-4 text-sm">
                {campaign.description}
                <span className="text-sizzle-orange font-semibold">{campaign.highlight}</span>
              </p>

              <ul className="space-y-2 mb-4">
                {campaign.points.map((point) => (
                  <li
                    key={point.text}
                    onClick={() => setActive({ kind: "point", point, campaign })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActive({ kind: "point", point, campaign });
                      }
                    }}
                    className="flex items-start gap-3 text-sm cursor-pointer rounded-lg p-2 -mx-2 hover:bg-sizzle-orange/10 transition-colors"
                  >
                    <point.icon className="w-4 h-4 text-sizzle-orange mt-0.5 shrink-0" aria-hidden="true" />
                    <span className="text-foreground/90">{point.text}</span>
                  </li>
                ))}
              </ul>

              <Button variant="sizzle" size="sm" asChild>
                <a
                  href={`https://wa.me/923303628777?text=${campaign.whatsappText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Order {campaign.title.split("–")[0].trim()} on WhatsApp
                </a>
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-4">
          <Calendar className="w-4 h-4" aria-hidden="true" />
          <span>Available daily — Main GT Road, Jhelum Cantt.</span>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" asChild>
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <section
      className="py-16 md:py-24 bg-background border-t border-border"
      aria-labelledby="deals-campaign-heading"
    >
      <div className="container mx-auto px-4">
        <header className="text-center mb-12">
          <p className="text-sizzle-orange font-medium tracking-widest uppercase mb-3 text-sm">
            Specials & Campaigns
          </p>
          <h2
            id="deals-campaign-heading"
            className="text-3xl md:text-4xl font-heading font-bold mb-4"
          >
            Exclusive offers & live <span className="text-gradient-fire">campaigns</span>
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Save more on every visit to Sizzlers Restaurant Jhelum. Panels rotate every 30 seconds — click any
            offer or campaign point to see full details.
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-8">
          {swapped ? (
            <>
              {campaignPanel}
              {dealsPanel}
            </>
          ) : (
            <>
              {dealsPanel}
              {campaignPanel}
            </>
          )}
        </div>
      </div>

      <Dialog open={active !== null} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {active?.kind === "deal" && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <active.deal.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sizzle-orange/15 text-sizzle-orange">
                    {active.deal.badge}
                  </span>
                </div>
                <DialogTitle className="text-2xl font-heading">{active.deal.title}</DialogTitle>
                <DialogDescription className="text-base">{active.deal.description}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-foreground/90 leading-relaxed">{active.deal.details}</p>
                {active.deal.points && (
                  <div>
                    <h5 className="font-semibold mb-2 text-sm">What's included:</h5>
                    <ul className="space-y-2">
                      {active.deal.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-foreground/85">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sizzle-orange shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button variant="sizzle" asChild>
                    <a
                      href={`https://wa.me/923303628777?text=${active.deal.whatsappText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Order on WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/menu">View Menu</Link>
                  </Button>
                </div>
              </div>
            </>
          )}
          {active?.kind === "point" && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-sizzle-orange/15 flex items-center justify-center shrink-0">
                    <active.point.icon className="w-5 h-5 text-sizzle-orange" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-sizzle-orange text-white">
                    {active.campaign.badge}
                  </span>
                </div>
                <DialogTitle className="text-2xl font-heading">{active.point.text}</DialogTitle>
                <DialogDescription className="text-base">Part of: {active.campaign.title}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="text-sm text-foreground/90 leading-relaxed">{active.point.details}</p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button variant="sizzle" asChild>
                    <a
                      href={`https://wa.me/923303628777?text=${active.campaign.whatsappText}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Order on WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/contact">Contact Us</Link>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default DealsCampaignSection;
